import React, { type ReactNode } from 'react';
import { View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { ThemedText } from '@/components/themed-text';
import { Card } from '@/components/ui/card';
import { cn } from '@/utils/cn';
import { createThemedStyles } from '@/hooks/use-theme-colors';

export type SectionTone = 'neutral' | 'success' | 'warning' | 'danger' | 'muted';

const useTone = createThemedStyles<Record<SectionTone, { bg: string; color: string }>>((th) => ({
  neutral: { bg: th('#FFF7ED'), color: th('#C2410C') },
  success: { bg: th('#ECFDF5'), color: th('#047857') },
  warning: { bg: th('#FFFBEB'), color: th('#B45309') },
  danger: { bg: th('#FEF2F2'), color: th('#B91C1C') },
  muted: { bg: th('#F5F5F5'), color: th('#737373') },
}));

export interface SectionCardProps {
  icon: React.ComponentProps<typeof Ionicons>['name'];
  title: string;
  /** Estado curto no canto direito do cabeçalho (ex.: "Agendável", "Pago"). */
  badge?: string;
  badgeTone?: SectionTone;
  children: ReactNode;
  className?: string;
}

/**
 * Bloco de leitura com cabeçalho próprio: ícone em círculo suave, título e
 * estado opcional. Padrão único para telas de detalhe — cada card diz o que é,
 * em vez de largar linhas soltas num retângulo branco.
 */
export function SectionCard({
  icon,
  title,
  badge,
  badgeTone = 'neutral',
  children,
  className,
}: SectionCardProps) {
  const TONE = useTone();
  const tone = TONE[badgeTone];

  return (
    <Card className={cn('p-5', className)}>
      <View className="flex-row items-center gap-2.5">
        <View className="h-8 w-8 items-center justify-center rounded-full bg-primary/10 dark:bg-primary">
          <Ionicons className="text-primary dark:text-white" name={icon} size={16} />
        </View>
        <ThemedText className="flex-1 font-bold text-sm text-neutral-900">{title}</ThemedText>
        {badge ? (
          <View className="rounded-full px-2.5 py-1" style={{ backgroundColor: tone.bg }}>
            <ThemedText className="font-semibold text-xs" style={{ color: tone.color }}>
              {badge}
            </ThemedText>
          </View>
        ) : null}
      </View>

      <View className="mt-4 gap-4">{children}</View>
    </Card>
  );
}

export interface FieldProps {
  label: string;
  /** Vazio vira o texto de `emptyText`, nunca um traço solto. */
  value?: string | null;
  emptyText?: string;
  hint?: string;
  className?: string;
}

/** Rótulo pequeno em cima, valor embaixo: valor longo não briga com a margem. */
export function Field({ label, value, emptyText = 'Não informado', hint, className }: FieldProps) {
  const filled = !!value && value.trim().length > 0;

  return (
    <View className={className}>
      <ThemedText className="text-xs text-neutral-500">{label}</ThemedText>
      <ThemedText
        selectable={filled}
        className={cn('font-semibold text-base', filled ? 'text-neutral-900' : 'text-neutral-400')}
      >
        {filled ? value : emptyText}
      </ThemedText>
      {hint ? <ThemedText className="text-xs leading-4 text-neutral-400">{hint}</ThemedText> : null}
    </View>
  );
}

export interface StatTileProps {
  label: string;
  value: string;
  /** Destaque do valor (ex.: preço em laranja). */
  emphasis?: boolean;
}

/** Número curto em caixa cinza — leitura de relance, lado a lado com outro. */
export function StatTile({ label, value, emphasis = false }: StatTileProps) {
  return (
    <View className="flex-1 rounded-2xl bg-neutral-50 p-3">
      <ThemedText className="text-xs text-neutral-500">{label}</ThemedText>
      <ThemedText
        className={cn('font-bold text-base', emphasis ? 'text-primary' : 'text-neutral-900')}
      >
        {value}
      </ThemedText>
    </View>
  );
}

/** Linha de StatTiles. */
export function StatRow({ children }: { children: ReactNode }) {
  return <View className="flex-row gap-3">{children}</View>;
}

export function SectionDivider() {
  return <View className="h-px bg-neutral-100" />;
}

/** Lista de etiquetas (formas de pagamento, tags) alinhada à esquerda. */
export function PillList({ label, items }: { label: string; items: string[] }) {
  return (
    <View className="gap-2">
      <ThemedText className="text-xs text-neutral-500">{label}</ThemedText>
      {items.length > 0 ? (
        <View className="flex-row flex-wrap gap-2">
          {items.map((item) => (
            <View key={item} className="rounded-full bg-primary/10 px-3 py-1.5">
              <ThemedText className="font-semibold text-xs text-primary">{item}</ThemedText>
            </View>
          ))}
        </View>
      ) : (
        <ThemedText className="text-sm text-neutral-400">Nada marcado.</ThemedText>
      )}
    </View>
  );
}
